import store from '@/store';
import axios from 'axios';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Mutations, Actions, Endpoints, ID } from '@/ts/store/Constants';
import PropertyData from '@/ts/PropertyData';
import CountryData from '@/ts/CountryData';
import BuildingData from '@/ts/BuildingData';
import PropertyManagerData from '@/ts/PropertyManagerData';
import { PropertyDataResponse, CountryDataResponse } from '@/ts/HococoInterfaces';
import UserManagement from '@/ts/store/modules/UserManagement';
import UserManagementData from '@/ts/UserManagementData';
import Buildings from './Buildings';
import ErrorResponse from '@/ts/ErrorResponse';
import TenancyPeriodData from '@/ts/TenancyPeriodData';
import TenancyPeriodActivityData from '@/ts/TenancyPeriodActivityData';
import router from '@/router';
import { NotifyHelpers } from '@/ts/helpers/NotifyHelpers';
import difference from 'lodash/difference';

@Module({name: 'Properties', namespaced: true})
export default class Properties extends VuexModule {
    public properties: PropertyData[] = [];
    public cachedProperty: PropertyData = null;
    public propertiesMap: { [index: number]: number } = {};
    public propertyId: number = null;
    public managers: PropertyManagerData[] = [];
    public managersMap: { [index: number]: number } = {};
    public managerData: PropertyManagerData | null = null;
    public managerSuggestions: number[] = [];
    public managerSearchQuery: string = '';
    public userSuggestionVisibility: boolean = false;
    public editFormVisible: boolean = false;
    public addUserFormVisible: boolean = false;
    public activePropertyId: number = null;
    public countries: CountryData[] = [];
    public countriesMap: { [index: number]: number } = {};
    public currentProperty: any = null;
    public newFloorPlanSrc: any = null;
    public tenancyPeriods: TenancyPeriodData[] = [];
    public tenancyPeriodsActivity: TenancyPeriodActivityData[] = [];
    public searchInitialized: boolean = false;

    @Mutation
    public RESET_PROPERTIES_STORE_STATE(): void {
        this.properties               = [];
        this.cachedProperty           = null;
        this.propertiesMap            = {};
        this.propertyId               = null;
        this.managers                 = [];
        this.managersMap              = {};
        this.managerData              = null;
        this.managerSuggestions       = [];
        this.managerSearchQuery       = '';
        this.userSuggestionVisibility = false;
        this.editFormVisible          = false;
        this.addUserFormVisible       = false;
        this.activePropertyId         = null;
        this.countries                = [];
        this.countriesMap             = {};
        this.currentProperty          = null;
        this.newFloorPlanSrc          = null;
        this.tenancyPeriods           = [];
        this.tenancyPeriodsActivity   = [];
        this.searchInitialized        = false;
    }

    get getNewFloorPlanSrc() {
        return this.newFloorPlanSrc;
    }

    get allProperties(): PropertyData[] {
        return this.properties.filter((p) => p.persisted);
    }

    get buildingsProperties() {
        return ((buildingsId: number[]) => {
            return this.properties.filter((property: PropertyData) => buildingsId.includes(property.buildingId));
        });
    }

    get projectsProperties() {
        return ((projectsId: number[]) => {
            const buildingIds = store.state.Buildings.buildings
                .filter((building: BuildingData) => projectsId.includes(building.projectId))
                .map((building: BuildingData) => building.id);

            return this.properties.filter((property: PropertyData) => buildingIds.includes(property.buildingId));
        });
    }

    get propertyIds() {
        return this.properties.map((property: PropertyData) => {
            return property.id;
        });
    }

    get getProperty() {
        return ((propertyId: number) => {
            return this.properties[this.propertiesMap[propertyId]];
        });
    }

    get getCurrentProperty() {
        return this.currentProperty;
    }

    get allCountries() {
        return this.countries;
    }

    get allPropertyManagers() {
        return this.managers;
    }

    get selectedProperties() {
        return this.properties.filter((item) => {
            return item.selected === true;
        });
    }

    get selectedPropertyIds() {
        return store.getters['Properties/selectedProperties']
            .map((property: PropertyData) => property.id);
    }

    get editMenuVisible(): boolean {
        return this.editFormVisible;
    }

    get addUserMenuVisible(): boolean {
        return this.addUserFormVisible;
    }

    get activeProperty() {
        if (this.activePropertyId === null) {
            return null;
        }

        return this.properties[this.propertiesMap[this.activePropertyId]];
    }

    get getPropertyId() {
        if (this.activePropertyId === null) {
            return null;
        }

        return this.properties[this.propertiesMap[this.activePropertyId]].id;
    }

    get getPropertyAddress() {
        if (this.activePropertyId === null) {
            return null;
        }

        return this.properties[this.propertiesMap[this.activePropertyId]].address;
    }

    get getPropertyCompanyNumber() {
        if (this.activePropertyId === null) {
            return null;
        }

        return this.properties[this.propertiesMap[this.activePropertyId]].companyNumber;
    }

    get getPropertyCompanyName() {
        if (this.activePropertyId === null) {
            return null;
        }

        return this.properties[this.propertiesMap[this.activePropertyId]].companyName;
    }

    get getPropertyCity() {
        if (this.activePropertyId === null) {
            return null;
        }

        return this.properties[this.propertiesMap[this.activePropertyId]].city;
    }

    get getPropertyZipCode() {
        if (this.activePropertyId === null) {
            return null;
        }

        return this.properties[this.propertiesMap[this.activePropertyId]].zipCode;
    }

    get getPropertyFloor() {
        if (this.activePropertyId === null) {
            return null;
        }

        return this.properties[this.propertiesMap[this.activePropertyId]].floor;
    }

    get getPropertyType() {
        if (this.activePropertyId === null) {
            return null;
        }

        return this.properties[this.propertiesMap[this.activePropertyId]].type;
    }

    get getPropertyManager() {
        if (this.activePropertyId === null) {
            return null;
        }

        return this.properties[this.propertiesMap[this.activePropertyId]].manager;
    }

    get getPropertyManagerName() {
        if (this.activePropertyId === null) {
            return null;
        }

        return this.properties[this.propertiesMap[this.activePropertyId]].managerName;
    }

    get getPropertyVariant() {
        if (this.activePropertyId === null) {
            return null;
        }

        return this.properties[this.propertiesMap[this.activePropertyId]].variantId;
    }

    get getPropertyCountry() {
        if (this.activePropertyId === null) {
            return null;
        }

        return this.properties[this.propertiesMap[this.activePropertyId]].countryId;
    }

    get getRent() {
        if (this.activePropertyId === null) {
            return null;
        }

        return this.properties[this.propertiesMap[this.activePropertyId]].rentAmount;
    }

    get getDeposit() {
        if (this.activePropertyId === null) {
            return null;
        }

        return this.properties[this.propertiesMap[this.activePropertyId]].depositAmount;
    }

    get getPropertyBuilding() {
        if (this.activePropertyId === null) {
            return null;
        }

        for (const building of Buildings.state.buildings) {
            if (building.selected === true) {
                this.properties[this.propertiesMap[this.activePropertyId]].buildingId = building.id;
            }
        }

        return this.properties[this.propertiesMap[this.activePropertyId]].buildingId;
    }

    get userSuggestionsVisible() {
        return this.userSuggestionVisibility;
    }

    get getManagerSuggestions() {
        return Properties.state.managerSuggestions;
    }

    get getCacheManager() {
        return (id: number) => {
            return Properties.state.managers[Properties.state.managersMap[id]];
        };
    }

    get getManagerId() {
        return (Properties.state.managerData !== null)
            ? Properties.state.managerData.id
            : ID.NO_USER;
    }

    get getPropertyTitleNumber() {
        if (this.activePropertyId === null) {
            return null;
        }

        return this.properties[this.propertiesMap[this.activePropertyId]].titleNumber;
    }

    @Action
    public EDIT_PROPERTY_COMPANY(data: any) {
        axios.patch(Endpoints.Admin.Properties.PROPERTY_COMPANIES(data.unit), data)
            .then((response) => {
                store.commit(Mutations.UPDATE_PROPERTY_COMPANY_DATA, {
                    companyName: response.data.company_name,
                    companyNumber: response.data.company_number,
                });

                store.commit(Mutations.SET_ADD_TENANT_VISIBILITY, false);

            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public ADD_NEW_COMPANY(data: any) {
        axios.post(Endpoints.Admin.Properties.PROPERTY_COMPANIES(data.unit), data)
            .then((response) => {
                store.commit(Mutations.UPDATE_PROPERTY_COMPANY_DATA, {
                    companyName: response.data.property.company_name,
                    companyNumber: response.data.property.company_number,
                });

                store.dispatch(Actions.FETCH_CURRENT_PROPERTY, router.currentRoute.params.unitId);

                // Add Primary Tenant
                if (data.withContact) {
                    store.commit(Mutations.ADD_TENANT_TO_PROPERTY, response.data.user);

                    store.commit(Mutations.UPDATE_ACTIVE_PROPERTY_DATES, {
                        propertyId: response.data.user.property.id,
                        vacantFrom: null,
                        moveInDate: response.data.user.property_roles[0].created_at,
                        releaseDate: null,
                    });

                    store.commit(Mutations.UPDATE_PROPERTY_OWNER, {
                        isNewOwner : true,
                        oldOwnedProperty : '',
                        newProperty: data.unit,
                        tenantId: response.data.user.id,
                    });

                    const userObject = {
                        userId: response.data.user.id,
                        roleId: response.data.user.property_roles[0].pivot.role_id,
                        firstName: response.data.user.person.first_name,
                        familyName: response.data.user.person.family_name,
                        phoneNumber: response.data.user.person.phone_number,
                    };

                    store.commit(Mutations.ADD_USER_TO_CACHE, userObject);

                    store.commit(Mutations.ADD_PROPERTY_TENANT_TO_CACHE, {
                        userId: userObject.userId,
                        roleId: userObject.roleId,
                        propertyId: data.unit,
                    });
                }

                store.commit(Mutations.SET_ADD_TENANT_VISIBILITY, false);

            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public INITIALIZE_PROPERTY_SEARCH() {
        store.search.registerResource('properties', {
            index: ['titleNumber', 'address'],
            getter: (state: any) => this.getters.allProperties,
            watch: { delay: 5000 },
        });

        Properties.state.searchInitialized = true;
    }

    @Action
    public INITIATE_PROPERTY() {
        store.commit(Mutations.INITIATE_NEW_PROPERTY);
    }

    @Action
    public FETCH_PROPERTIES() {
        if (!Properties.state.searchInitialized) {
            store.dispatch(Actions.INITIALIZE_PROPERTY_SEARCH);
        }

        return axios.get(Endpoints.Admin.Properties.PROPERTIES)
            .then((response) => {
                store.commit(Mutations.ADD_PROPERTIES, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public POST_PROPERTIES() {
        const property = Properties.state.properties[
                            Properties.state.propertiesMap[
                                Properties.state.activePropertyId]];

        const requestData  = property.getRequestData();

        axios.post(Endpoints.Admin.Properties.PROPERTIES, requestData)
            .then((response) => {
                store.commit(Mutations.UPDATE_PERSISTED_PROPERTY, response.data.id);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public UPLOAD_PROPERTY_FLOOR_PLAN(data: any) {
        axios.post(Endpoints.Admin.Properties.PROPERTY_FLOOR_PLAN(data.propertyId), { floor: data.file })
            .then((response) => {
                store.commit(Mutations.UPDATE_PROPERTY_FLOOR_PLAN, response.data);
                store.commit(Mutations.SET_NEW_FLOOR_PLAN_SOURCE, null);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public UPDATE_PROPERTY_DATES(data: any) {
        axios.post(Endpoints.Admin.Properties.PROPERTY_DATES(data.propertyId), data)
            .then((response) => {
                store.commit(Mutations.SET_CURRENT_PROPERTY, response.data.property);

                NotifyHelpers.notify('unit_dates_has_been_updated_successfully', 'positive');
                store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
                store.commit(Mutations.REFRESH_PROPERTY_CACHED_TENANTS, response.data.property);

                store.commit(Mutations.UPDATE_ACTIVE_PROPERTY_DATES, {
                    propertyId: response.data.property.id,
                    vacantFrom: response.data.property.vacantFrom,
                    moveInDate: response.data.property.moveInDate,
                    releaseDate: response.data.property.releaseDate,
                });

            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public EDIT_PROPERTY(data: any) {
        const property = Properties.state.properties[
            Properties.state.propertiesMap[Properties.state.activePropertyId]
        ];

        if (property.dataEquals(Properties.state.cachedProperty)) {
            return store.commit(Mutations.HIDE_EDIT_PROPERTY_FORM);
        }

        axios.patch(Endpoints.Admin.Properties.SINGLE_PROPERTY(property.id), property.getRequestData())
            .then((response) => {
                if (data.fromLandingPage) {
                    store.dispatch(Actions.FETCH_CURRENT_PROPERTY, data.unitId);
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_CURRENT_PROPERTY(id: number) {
        axios.get(Endpoints.Admin.Properties.SINGLE_PROPERTY(id))
            .then((response) => {
                store.commit(Mutations.SET_CURRENT_PROPERTY, response.data.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public QUERY_PROPERTY_MANAGER_SUGGESTIONS() {
        const requestData = {
            searchQuery: Properties.state.managerSearchQuery,
        };

        const buildingId = Properties.state.properties[
            Properties.state.propertiesMap[
                Properties.state.activePropertyId]].buildingId;

        if (!buildingId) {
            return;
        }

        axios.post(Endpoints.Admin.Buildings.PROPERTY_MANAGER_SUGGESTIONS(buildingId), requestData)
            .then((response) => {
                if (response.data.suggestions != null && response.data.suggestions.length > 0) {
                    store.commit(Mutations.ADD_PROPERTY_MANAGER_SUGGESTIONS, response.data.suggestions);
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public DELETE_UNIT(data: any) {
        axios.delete(Endpoints.Admin.Properties.SINGLE_PROPERTY(data.unitId))
            .then((response) => {
                if (response.data) {
                    store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
                    store.commit(Mutations.REMOVE_UNIT, data.unitId);
                }
            })
            .catch((error) => {
                if (error.response) {
                    store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });

        return;
    }

    @Mutation
    public REMOVE_UNIT(unitId: number) {
        this.properties = this.properties.filter((property: any) => {
            return property.id !== unitId;
        });

        this.propertiesMap = {};
        for (const [i, event] of this.properties.entries()) {
            this.propertiesMap[event.id] = i;
        }
    }

    @Mutation
    public REMOVE_PROPERTY_TENANTS(propertyId: number) {
        this.properties[this.propertiesMap[propertyId]].evictTenants();
    }

    @Mutation
    public UPDATE_PROPERTY_OWNER(data: any) {
        if (data.isNewOwner) {
            this.properties[this.propertiesMap[data.newProperty]].updateOwner(data.tenantId);
            if (data.oldOwnedProperty !== '') {
                this.properties[this.propertiesMap[data.oldOwnedProperty]].removeOwner();
            }
        }
        if (!data.isNewOwner && data.oldOwnedProperty !== '') {
            this.properties[this.propertiesMap[data.oldOwnedProperty]].removeOwner();
        }
    }

    @Mutation
    public ADD_MANAGER_TO_PROPERTY(managerId: number) {
        this.properties[this.propertiesMap[this.activePropertyId]].addManager(managerId);
        store.commit(Mutations.HIDE_PROPERTY_USER_SUGGESTIONS);
        store.commit(Mutations.SET_PROPERTY_MANAGER_SEARCH_QUERY, '');
    }

    @Mutation
    public REMOVE_MANAGER_FROM_ACTIVE_PROPERTY(managerId: number) {
        this.properties[this.propertiesMap[this.activePropertyId]].removeManager(managerId);
    }

    @Action
    public FETCH_COUNTRIES() {
        axios.get(Endpoints.Admin.Countries.COUNTRY)
            .then((response) => {
                if (response.data != null && response.data.length > 0) {
                    store.commit(Mutations.ADD_COUNTRIES, response.data);
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
        return;
    }

    @Mutation
    public ADD_PROPERTY(property: PropertyDataResponse): void {
        let arrayIndex = null;

        if (!this.propertiesMap.hasOwnProperty(property.id)) {
            arrayIndex = this.properties.push(new PropertyData(
                property.id,
                property.address,
                property.city,
                property.zipCode,
                property.floor,
                property.type,
                property.variantId,
                property.countryId,
                property.buildingId,
                property.titleNumber,
            )) - 1;

            this.propertiesMap[property.id] = arrayIndex;

            if (property.guestsEnabled) {
                this.properties[this.propertiesMap[property.id]].guestsEnabled = true;
            }
        }
    }

    @Mutation
    public ADD_PROPERTIES(propertyData: PropertyDataResponse[]): void {
        if (!propertyData.length) {
            this.properties    = [];
            this.propertiesMap = {};
        }

        for (const property of propertyData) {
            let arrayIndex = null;

            if (!this.propertiesMap.hasOwnProperty(property.id)) {
                arrayIndex = this.properties.push(new PropertyData(
                    property.id,
                    property.address,
                    property.city,
                    property.zipCode,
                    property.floor,
                    property.type,
                    property.variantId,
                    property.countryId,
                    property.buildingId,
                    property.titleNumber,
                    property.rentAmount,
                    property.depositAmount,
                    property.unitType,
                    property.moveInDate,
                    property.releaseDate,
                    property.vacantFrom,
                    property.futureRent,
                    property.heatingCost,
                    property.waterCost,
                    property.electricityCost,
                    property.prepaidRent,
                    property.internetCost,
                    property.ubTotal,
                    property.area,
                    property.directOwnerId,
                    property.externalId,
                    property.companyNumber,
                    property.companyName,
                    property.isCommercial,
                    property.rooms,
                    property.projectName,
                    property.guestsEnabled,
                )) - 1;

                this.propertiesMap[property.id] = arrayIndex;
            } else {
                arrayIndex             = this.propertiesMap[property.id];
                const existingProperty = this.properties[arrayIndex];

                if (existingProperty) {
                    existingProperty.updateLocalPropertyData(property);
                }
            }

            if (property.associatedUsers !== null) {
                const associatedUsers = property.associatedUsers.split('|');

                const userObjects = associatedUsers.map((item) => {
                    const splitData = item.split(':');

                    return {
                        userId: parseInt(splitData[0], 10),
                        roleId: parseInt(splitData[1], 10),
                        firstName: splitData[2],
                        familyName: splitData[3],
                        phoneNumber: splitData[4],
                    };
                });

                store.commit(Mutations.ADD_USERS_TO_CACHE, userObjects);

                for (const associatedUser of userObjects) {
                    this.properties[arrayIndex].addAuthorization(
                        associatedUser.userId,
                        associatedUser.roleId,
                    );
                }
            }
        }

        const responsePropertiesIds = propertyData.map((v) => v.id);
        const localPropertyIds      = this.properties.map((v) => v.id);
        const propertiesToRemove    = difference(localPropertyIds, responsePropertiesIds);

        for (const unit of propertiesToRemove) {
            store.commit(Mutations.REMOVE_UNIT, unit);
        }
    }

    @Mutation
    public ADD_PROPERTY_USER(user: UserManagementData) {
        let arrayIndex = null;

        if (!UserManagement.state.usersMap.hasOwnProperty(user.id)) {
            arrayIndex = UserManagement.state.users.push(user) - 1;

            UserManagement.state.usersMap[user.id] = arrayIndex;
        } else {
            arrayIndex = UserManagement.state.usersMap[user.id];
        }

        return arrayIndex;
    }

    @Mutation
    public ADD_COUNTRIES(countryData: CountryDataResponse[]): void {
        let arrayIndex = null;
        
        for (const country of countryData) {
            console.log(country);
            if (!this.countriesMap.hasOwnProperty(country.id)) {
                arrayIndex = this.countries.push(new CountryData(
                    country.id,
                    country.language_id,
                    country.currency_id,
                    country.local_name,
                    country.english_name,
                )) - 1;

                this.countriesMap[country.id] = arrayIndex;
            }
        }
    }

    @Mutation
    public UPDATE_ACTIVE_PROPERTY(propertyId: number): void {
        this.activePropertyId = propertyId;
    }

    @Mutation
    public EDIT_ACTIVE_PROPERTY(): void {
        this.cachedProperty = JSON.parse(JSON.stringify(this.properties[this.propertiesMap[this.activePropertyId]]));
    }

    @Mutation
    public CANCEL_EDIT_PROPERTY(): void {
        this.properties[this.propertiesMap[this.activePropertyId]].cloneData(this.cachedProperty);
        this.cachedProperty = null;
    }

    @Mutation
    public INITIATE_NEW_PROPERTY() {
        const activeIndex = this.properties.push(new PropertyData(-1)) - 1;
        this.propertiesMap[-1] = activeIndex;
        this.activePropertyId = -1;
    }

    @Mutation
    public SHOW_EDIT_PROPERTY_FORM() {
        this.editFormVisible = true;
    }

    @Mutation
    public HIDE_EDIT_PROPERTY_FORM() {
        this.editFormVisible = false;
    }

    @Mutation
    public SHOW_ADD_USER_TO_PROPERTY_FORM() {
        this.addUserFormVisible = true;
    }

    @Mutation
    public HIDE_ADD_USER_TO_PROPERTY_FORM() {
        this.addUserFormVisible = false;
    }

    @Mutation
    public SHOW_PROPERTY_USER_SUGGESTIONS(): void {
        this.userSuggestionVisibility = true;
    }

    @Mutation
    public HIDE_PROPERTY_USER_SUGGESTIONS(): void {
        this.userSuggestionVisibility = false;
    }

    @Mutation
    public ADD_PROPERTY_TENANT_TO_CACHE(userData: any): void {
        this.properties[this.propertiesMap[userData.propertyId]].addAuthorization(
            userData.userId,
            userData.roleId,
        );
    }

    @Mutation
    public REMOVE_PROPERTY_TENANT_FROM_CACHE(userData: any): void {
        this.properties[this.propertiesMap[userData.propertyId]].removeAuthorization(
            userData.userId,
            userData.roleId,
        );
    }

    @Mutation
    public REFRESH_PROPERTY_CACHED_TENANTS(property: any): void {
        const cachedProperty = this.properties[this.propertiesMap[property.id]];

        cachedProperty.emptyTenants();
        cachedProperty.emptyAuthorizedUsers();
        cachedProperty.updateOwner(property.propertyOwner);

        if (property.residents.length) {
            const userObjects = property.residents.map((user: any) => {
                return {
                    userId: user.id,
                    roleId: user.pivot.role_id,
                    firstName: typeof user.person.first_name !== 'undefined' ? user.person.first_name : user.name,
                    familyName: typeof user.person.family_name !== 'undefined' ? user.person.family_name : '',
                    phoneNumber: typeof user.person.phone_number !== 'undefined' ? user.person.phone_number : '',
                };
            });

            for (const user of userObjects) {
                cachedProperty.addAuthorization(
                    user.userId,
                    user.roleId,
                );
            }
        }
    }

    @Mutation
    public UPDATE_PROPERTY_ADDRESS(value: string): void {
        this.properties[this.propertiesMap[this.activePropertyId]].address = value;
    }

    @Mutation
    public UPDATE_PROPERTY_CITY(value: string): void {
        this.properties[this.propertiesMap[this.activePropertyId]].city = value;
    }

    @Mutation
    public UPDATE_PROPERTY_ZIP_CODE(value: number): void {
        this.properties[this.propertiesMap[this.activePropertyId]].zipCode = value;
    }

    @Mutation
    public UPDATE_PROPERTY_FLOOR(value: number): void {
        this.properties[this.propertiesMap[this.activePropertyId]].floor = value;
    }

    @Mutation
    public UPDATE_PROPERTY_TYPE(value: string): void {
        this.properties[this.propertiesMap[this.activePropertyId]].type = value;
    }

    @Mutation
    public UPDATE_PROPERTY_MANAGER(value: number): void {
        this.properties[this.propertiesMap[this.activePropertyId]].manager = value;
    }

    @Mutation
    public UPDATE_PROPERTY_RENT(value: number): void {
        this.properties[this.propertiesMap[this.activePropertyId]].rentAmount = value;
    }

    @Mutation
    public UPDATE_PROPERTY_DEPOSIT(value: number): void {
        this.properties[this.propertiesMap[this.activePropertyId]].depositAmount = value;
    }

    @Mutation
    public UPDATE_PROPERTY_MANAGER_NAME(value: string): void {
        this.properties[this.propertiesMap[this.activePropertyId]].managerName = value;
    }

    @Mutation
    public UPDATE_PROPERTY_VARIANT_ID(value: number): void {
        this.properties[this.propertiesMap[this.activePropertyId]].variantId = value;
    }

    @Mutation
    public UPDATE_PROPERTY_COUNTRY_ID(value: number): void {
        this.properties[this.propertiesMap[this.activePropertyId]].countryId = value;
    }

    @Mutation
    public UPDATE_PROPERTY_BUILDING_ID(value: number): void {
        this.properties[this.propertiesMap[this.activePropertyId]].buildingId = value;
    }

    @Mutation
    public UPDATE_PROPERTY_TITLE_NUMBER(value: string): void {
        this.properties[this.propertiesMap[this.activePropertyId]].titleNumber = value;
    }

    @Mutation
    public UPDATE_PROPERTY_COMPANY_NUMBER(value: string|null): void {
        this.properties[this.propertiesMap[this.activePropertyId]].companyNumber = value;
    }

    @Mutation
    public UPDATE_PROPERTY_COMMERCIAL_TYPE(isCommercial: boolean): void {
        this.properties[this.propertiesMap[this.activePropertyId]].isCommercial = isCommercial;
    }

    @Mutation
    public UPDATE_PROPERTY_COMPANY_NAME(value: string|null): void {
        this.properties[this.propertiesMap[this.activePropertyId]].companyName = value;
    }

    @Mutation
    public UPDATE_PROPERTY_WATER_COST(value: number): void {
        this.properties[this.propertiesMap[this.activePropertyId]].waterCost = value;
    }

    @Mutation
    public UPDATE_PROPERTY_HEATING_COST(value: number): void {
        this.properties[this.propertiesMap[this.activePropertyId]].heatingCost = value;
    }

    @Mutation
    public UPDATE_PROPERTY_AREA(value: number): void {
        this.properties[this.propertiesMap[this.activePropertyId]].area = value;
    }

    @Mutation
    public UPDATE_PROPERTY_ROOMS(value: number): void {
        this.properties[this.propertiesMap[this.activePropertyId]].rooms = value;
    }

    @Mutation
    public UPDATE_PROPERTY_POWER_COST(value: number): void {
        this.properties[this.propertiesMap[this.activePropertyId]].electricityCost = value;
    }

    @Mutation
    public UPDATE_PROPERTY_INTERNET_COST(value: number): void {
        this.properties[this.propertiesMap[this.activePropertyId]].internetCost = value;
    }

    @Mutation
    public UPDATE_PROPERTY_PREPAID_RENT(value: number): void {
        this.properties[this.propertiesMap[this.activePropertyId]].prepaidRent = value;
    }

    @Mutation
    public UPDATE_PROPERTY_FUTURE_RENT(value: number): void {
        this.properties[this.propertiesMap[this.activePropertyId]].futureRent = value;
    }

    @Mutation
    public UPDATE_PROPERTY_TOTAL_PAID(value: number): void {
        this.properties[this.propertiesMap[this.activePropertyId]].ubTotal = value;
    }

    @Mutation
    public UPDATE_PROPERTY_UNIT_TYPE_UB(value: string): void {
        this.properties[this.propertiesMap[this.activePropertyId]].unitType = value;
    }

    @Mutation
    public UPDATE_PROPERTY_VACANT_FROM_DATE(value: string): void {
        this.properties[this.propertiesMap[this.activePropertyId]].vacantFrom = value;
    }

    @Mutation
    public UPDATE_PROPERTY_RELEASE_DATE(value: string): void {
        this.properties[this.propertiesMap[this.activePropertyId]].releaseDate = value;
    }

    @Mutation
    public UPDATE_PROPERTY_MOVING_IN_DATE(value: string): void {
        this.properties[this.propertiesMap[this.activePropertyId]].moveInDate = value;
    }

    @Mutation
    public ADD_PROPERTY_MANAGER_SUGGESTIONS(propertyManager: any) {
        let arrayIndex = null;

        this.managerSuggestions = [];

        for (const manager of propertyManager) {
            if (!this.managersMap.hasOwnProperty(manager.user_id)) {
                arrayIndex = this.managers.push(new PropertyManagerData(
                    manager.user_id,
                    manager.first_name,
                    manager.family_name,
                    manager.role_id,
                )) - 1;

                this.managersMap[manager.user_id] = arrayIndex;

                this.managersMap[manager.user_id] = arrayIndex;
                this.managerSuggestions.push(manager.user_id);
            } else {
                arrayIndex = this.managersMap[manager.user_id];

                this.managerSuggestions.push(manager.user_id);
            }
        }

        return arrayIndex;
    }

    @Mutation
    public SELECT_PROPERTY(itemId: number) {
        this.properties[this.propertiesMap[itemId]].selected = true;
    }

    @Mutation
    public DESELECT_PROPERTY(itemId: number) {
        this.properties[this.propertiesMap[itemId]].selected = false;
    }

    @Mutation
    public RESET_PROPERTY_SELECTION() {
        for (const property of this.properties) {
            property.selected = false;
        }
    }

    @Mutation
    public SET_PROPERTY_MANAGER_SEARCH_QUERY(value: string) {
        this.managerSearchQuery = value;
    }

    @Mutation
    public UPDATE_PERSISTED_PROPERTY(propertyId: number) {
        const currentArrayIndex = this.propertiesMap[this.activePropertyId];
        this.activePropertyId = null;
        this.propertiesMap[propertyId] = currentArrayIndex;
        this.properties[currentArrayIndex].id = propertyId;
        delete this.propertiesMap[-1];
    }

    @Mutation
    public SET_CURRENT_PROPERTY(data: any): void {
        this.currentProperty = data;
    }

    @Mutation
    public UPDATE_PROPERTY_COMPANY_DATA(companyData: any) {
        this.currentProperty.companyName   = companyData.companyName;
        this.currentProperty.companyNumber = companyData.companyNumber;

        this.properties[this.propertiesMap[this.currentProperty.id]].updateCompanyData({
            companyName: companyData.companyName,
            companyNumber: companyData.companyNumber,
        });
    }

    @Mutation
    public ADD_TENANT_TO_PROPERTY(tenant: any) {
        if (tenant.tenantStatus === 'primary') {
            this.currentProperty.propertyOwner = tenant.id;
            this.currentProperty.residents.unshift(tenant);
        } else {
            this.currentProperty.residents.push(tenant);
        }
    }

    @Mutation
    public ADD_UPCOMING_TENANT(tenant: any) {
        const newUpcomingResident = tenant.queuedMoveIn;

        delete tenant.queuedMoveIn;
        newUpcomingResident.owner = tenant;

        this.currentProperty.upcomingResidents.push(newUpcomingResident);
    }

    @Mutation
    public EDIT_UPCOMING_TENANT(data: any) {
        const upcomingResident = this.currentProperty.upcomingResidents.find((resident: any) => resident.id === data.queuedMoveIn.id);

        upcomingResident.company_name   = data.queuedMoveIn.company_name;
        upcomingResident.company_number = data.queuedMoveIn.company_number;
        upcomingResident.planned_at     = data.queuedMoveIn.planned_at;

        upcomingResident.owner.name  = data.name;
        upcomingResident.owner.email = data.email;

        upcomingResident.owner.person.first_name   = data.person.first_name;
        upcomingResident.owner.person.family_name  = data.person.family_name;
        upcomingResident.owner.person.phone_number = data.person.phone_number;
    }

    @Mutation
    public ADD_UPCOMING_GUEST(guest: any) {
        this.currentProperty.upcomingGuests.push(guest);
    }

    @Mutation
    public EDIT_UPCOMING_GUEST(data: any) {
        const upcomingResident = this.currentProperty.upcomingGuests.find((guest: any) => guest.id === data.id);

        upcomingResident.move_in_at     = data.move_in_at;
        upcomingResident.move_out_at     = data.move_out_at;
        upcomingResident.email = data.email;

        upcomingResident.first_name   = data.first_name;
        upcomingResident.family_name  = data.family_name;
        upcomingResident.phone_number = data.phone_number;
    }

    @Mutation
    public REMOVE_TENANT_FROM_UNIT(id: number) {
        this.currentProperty.residents = this.currentProperty.residents.filter((secondaryTenant: any) => {
            return secondaryTenant.id !== id;
        });
    }

    @Mutation
    public REMOVE_DOCUMENT_FROM_UNIT(documentId: number) {
        this.currentProperty.contracts = this.currentProperty.contracts.filter((contract: any) => {
            return contract.id !== documentId;
        });

        this.currentProperty.documents = this.currentProperty.documents.filter((document: any) => {
            return document.id !== documentId;
        });
    }

    @Mutation
    public ADD_PROPERTY_CONTRACT(data: any) {
        this.currentProperty.contracts.unshift(data);
    }

    @Mutation
    public ADD_PROPERTY_DOCUMENT(data: any) {
        this.currentProperty.documents.unshift(data);
    }

    @Mutation
    public UPDATE_PROPERTY_FLOOR_PLAN(data: any) {
        this.currentProperty.floorPlan = data;
    }

    @Mutation
    public SET_NEW_FLOOR_PLAN_SOURCE(data: any) {
        this.newFloorPlanSrc = data;
    }

    @Mutation
    public UPDATE_ACTIVE_PROPERTY_DATES(data: any) {
        const unitLandingProperty = this.properties.filter((property: PropertyData) => {
            return property.id === data.propertyId;
        });

        for (const property of this.properties) {
            if (property.id === data.propertyId) {
                property.vacantFrom  = data.vacantFrom;
                property.moveInDate  = data.moveInDate;
                property.releaseDate = data.releaseDate;
            }
        }
    }

    @Mutation
    public UPDATE_TENANCY_PERIODS(periods: TenancyPeriodData[]) {
        this.tenancyPeriods = periods;
    }

    @Mutation
    public UPDATE_TENANCY_PERIODS_ACTIVITY(activity: TenancyPeriodActivityData[]) {
        this.tenancyPeriodsActivity = [];
        activity.forEach((it: any) => this.tenancyPeriodsActivity.push(it));
    }
}

